import type { AchievementId } from '@tg/shared';
import {
  ball,
  body,
  edge,
  fade,
  gearPath,
  gloss,
  mix,
  n,
  shade,
  shadowInk,
  star,
  tint,
  type ArtPal,
} from '../covers/kit';

/*
 * 27 achievement icons + 2 moment icons. Each builder draws inside the medal field (circle r ≈ 44 around (64, 64)),
 * one light source top-left (gradients from kit.ts + a gloss), soft shadow below, outlines = darker own colour.
 * Every colour is derived from the palette, so the muted "not earned" palette greys the whole icon.
 * No text, no coins/chips/money; Chinese motifs are drawn as objects, not as characters.
 */
export type IconBuilder = (P: ArtPal, p: string) => string;

/* ---------------- helpers ---------------- */

const D = (...s: string[]) => `<defs>${s.join('')}</defs>`;
const ol = (c: string, P: ArtPal, w = 2) =>
  `stroke="${edge(c, P)}" stroke-width="${w}" stroke-linejoin="round"`;
const u = (p: string, k: string) => `fill="url(#${p}${k})"`;
const sh = (P: ArtPal, p: string) => fade(`${p}s`, shadowInk(P), P.dark ? 0.55 : 0.2);
const gr = (p: string, cx: number, cy: number, rx: number, ry = 4) =>
  `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="url(#${p}s)"/>`;
const clip = (p: string) => `<clipPath id="${p}k"><circle cx="64" cy="64" r="46"/></clipPath>`;
const line = (d: string, c: string, w: number, o = 1) =>
  `<path d="${d}" fill="none" stroke="${c}" stroke-width="${w}" stroke-linecap="round" stroke-linejoin="round"${o < 1 ? ` stroke-opacity="${o}"` : ''}/>`;
/** a stroke drawn twice (outline + colour) — reads as a small tube with an edge */
const tube = (d: string, c: string, P: ArtPal, w: number) => line(d, edge(c, P), w + 2.4) + line(d, c, w);
const sparkle = (x: number, y: number, r: number, c: string, o = 0.9) =>
  `<polygon points="${star(x, y, r, r * 0.32, 4)}" fill="${c}" fill-opacity="${o}"/>`;
/** lighten keeping saturation (HSL) — the light-theme gold #B7791F tinted with white turns tan, this keeps it golden */
function lift(c: string, dl: number): string {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(c.slice(i, i + 2), 16) / 255) as [number, number, number];
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  const l = (mx + mn) / 2;
  const d = mx - mn;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (d) h = mx === r ? ((g - b) / d + 6) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  const L = Math.min(0.92, l + dl);
  const C = (1 - Math.abs(2 * L - 1)) * s;
  const X = C * (1 - Math.abs((h % 2) - 1));
  const m = L - C / 2;
  const k = Math.floor(h);
  const rgb = [
    [C, X, 0],
    [X, C, 0],
    [0, C, X],
    [0, X, C],
    [X, 0, C],
    [C, 0, X],
  ][k % 6]!;
  return (
    '#' +
    rgb
      .map((v) =>
        Math.round((v + m) * 255)
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
      .toUpperCase()
  );
}
/** icon gold: the palette gold, lifted on the light theme so stars / arrows stay golden, not tan */
const sun = (P: ArtPal) => (P.dark ? P.gold : lift(P.gold, 0.14));
/** cream paper / ivory (derived from the palette so the muted palette greys it too) */
const paper = (P: ArtPal) => (P.dark ? mix(tint(P.gold, 0.8), P.ink, 0.35) : tint(P.gold, 0.86));
const soil = (P: ArtPal) => mix(P.orange, P.dark ? '#2A1606' : '#5A2E0C', 0.5);
const steel = (P: ArtPal) => (P.dark ? mix(P.ink, P.blue, 0.25) : tint(mix(P.ink, P.blue, 0.4), 0.5));

/**
 * Koi / carp facing right, drawn around (64, 60) in a ~76 px box; place it with an SVG transform.
 * Needs a body gradient `${p}f`.
 */
function koi(P: ArtPal, p: string, tf: string, bodyC: string, spot: string): string {
  const fin = tint(P.orange, 0.1);
  return (
    `<g transform="${tf}">` +
    `<path d="M42 60L22 44Q30 60 22 77Z" fill="${fin}" ${ol(P.orange, P, 1.6)}/>` +
    `<path d="M58 46Q68 32 82 45Z" fill="${fin}" ${ol(P.orange, P, 1.4)}/>` +
    `<path d="M96 60C96 49 86 43 73 43C58 43 46 51 38 60C46 69 58 77 73 77C86 77 96 71 96 60Z" ${u(p, 'f')} ${ol(bodyC, P, 2)}/>` +
    `<path d="M72 72Q68 86 56 86Q63 78 64 72Z" fill="${fin}" ${ol(P.orange, P, 1.4)}/>` +
    `<ellipse cx="70" cy="52" rx="8" ry="5" fill="${spot}"/><ellipse cx="54" cy="63" rx="6" ry="4" fill="${spot}"/>` +
    `<path d="M84 49Q79 60 84 71" fill="none" stroke="${edge(bodyC, P)}" stroke-opacity=".45" stroke-width="1.4"/>` +
    `<circle cx="89" cy="55" r="2.6" fill="${shade(P.ink, P.dark ? 0.85 : 0)}"/><circle cx="88.2" cy="54.2" r=".9" fill="#FFFFFF"/>` +
    line('M95 64Q100 68 99 74', edge(bodyC, P), 1.2, 0.7) +
    gloss(70, 47, 12, 2.6, -6, 0.55) +
    `</g>`
  );
}

/** small round lantern (cx, cy = centre of the body, s = scale) */
function roundLantern(P: ArtPal, p: string, cx: number, cy: number, s: number): string {
  const g = sun(P);
  const rx = 30 * s;
  const ry = 25 * s;
  const cw = 26 * s;
  const ribs = [0.4, 0.8].map(
    (k) =>
      `M${n(cx)} ${n(cy - ry)}C${n(cx - rx * k)} ${n(cy - ry * 0.6)} ${n(cx - rx * k)} ${n(cy + ry * 0.6)} ${n(cx)} ${n(cy + ry)}C${n(cx + rx * k)} ${n(cy + ry * 0.6)} ${n(cx + rx * k)} ${n(cy - ry * 0.6)} ${n(cx)} ${n(cy - ry)}`,
  );
  return (
    `<circle cx="${n(cx)}" cy="${n(cy)}" r="${n(rx * 1.35)}" ${u(p, 'h')}/>` +
    line(`M${n(cx)} ${n(cy - ry - 16 * s)}V${n(cy - ry)}`, edge(g, P), 2.2) +
    `<rect x="${n(cx - cw / 2)}" y="${n(cy - ry - 8 * s)}" width="${n(cw)}" height="${n(9 * s)}" rx="${n(3 * s)}" ${u(p, 'g')} ${ol(g, P, 1.6)}/>` +
    `<rect x="${n(cx - cw / 2)}" y="${n(cy + ry - 1)}" width="${n(cw)}" height="${n(8 * s)}" rx="${n(3 * s)}" ${u(p, 'g')} ${ol(g, P, 1.6)}/>` +
    `<ellipse cx="${n(cx)}" cy="${n(cy)}" rx="${n(rx)}" ry="${n(ry)}" ${u(p, 'b')} ${ol(P.red, P, 2)}/>` +
    `<path d="${ribs.join('')}M${n(cx)} ${n(cy - ry)}V${n(cy + ry)}" fill="none" stroke="${shade(P.red, 0.35)}" stroke-width="1.3" stroke-opacity=".6"/>` +
    line(
      `M${n(cx)} ${n(cy + ry + 7 * s)}v${n(12 * s)}M${n(cx - 4 * s)} ${n(cy + ry + 8 * s)}l${n(-2 * s)} ${n(10 * s)}M${n(cx + 4 * s)} ${n(cy + ry + 8 * s)}l${n(2 * s)} ${n(10 * s)}`,
      g,
      2,
    ) +
    gloss(cx - rx * 0.4, cy - ry * 0.45, rx * 0.3, ry * 0.2, -35, 0.6)
  );
}
const lanternDefs = (P: ArtPal, p: string) =>
  ball(`${p}b`, P.red, 0.5, 0.4) +
  body(`${p}g`, sun(P), 0.5, 0.35, 1, 0) +
  fade(`${p}h`, P.orange, P.dark ? 0.45 : 0.3);

/** 5-petal plum blossom */
const blossom = (P: ArtPal, x: number, y: number, r: number) => {
  let s = '';
  for (let i = 0; i < 5; i++) {
    const a = (i * 2 * Math.PI) / 5 - Math.PI / 2;
    s += `<circle cx="${n(x + r * Math.cos(a))}" cy="${n(y + r * Math.sin(a))}" r="${n(r * 0.78)}"/>`;
  }
  return `<g fill="${tint(P.pink, 0.25)}" stroke="${edge(P.pink, P)}" stroke-width="1.1">${s}</g><circle cx="${x}" cy="${y}" r="${n(r * 0.45)}" fill="${sun(P)}"/>`;
};

/** 5-point star with a ball gradient `${p}${k}` */
const bigStar = (P: ArtPal, p: string, k: string, x: number, y: number, R: number) =>
  `<polygon points="${star(x, y, R, R * 0.45, 5)}" ${u(p, k)} ${ol(sun(P), P, 1.8)}/>` +
  gloss(x - R * 0.25, y - R * 0.3, R * 0.25, R * 0.12, -35, 0.6);

/* ---------------- icons ---------------- */

const firstRound: IconBuilder = (P, p) =>
  D(sh(P, p), body(`${p}l`, P.green, 0.45, 0.35, 0.6, 1), body(`${p}d`, soil(P), 0.3, 0.35, 0.5, 1)) +
  gr(p, 64, 100, 28) +
  `<path d="M34 98C38 82 90 82 94 98Z" ${u(p, 'd')} ${ol(soil(P), P)}/>` +
  tube('M64 88C64 76 62 68 64 58', P.green, P, 4) +
  `<path d="M63 67C54 50 38 47 28 53C34 68 51 74 63 67Z" ${u(p, 'l')} ${ol(P.green, P)}/>` +
  `<path d="M65 59C70 38 88 30 100 34C98 54 81 65 65 59Z" ${u(p, 'l')} ${ol(P.green, P)}/>` +
  line('M60 65C52 59 43 56 34 55M68 57C76 48 84 43 93 39', '#FFFFFF', 1.6, 0.45) +
  gloss(84, 42, 7, 3, -35, 0.55) +
  gloss(40, 54, 5, 2.2, 20, 0.45);

const firstWin: IconBuilder = (P, p) =>
  D(
    clip(p),
    body(`${p}h`, P.green, 0.4, 0.35, 0.5, 1),
    body(`${p}f`, P.red, 0.4, 0.3, 0.6, 1),
    ball(`${p}n`, sun(P), 0.5, 0.35),
  ) +
  `<g clip-path="url(#${p}k)"><path d="M14 104C30 70 98 70 114 104V112H14Z" ${u(p, 'h')} ${ol(P.green, P)}/></g>` +
  gloss(46, 80, 11, 3, -15, 0.35) +
  tube('M58 78V30', sun(P), P, 3.2) +
  `<path d="M60 32C72 26 82 38 98 31L93 50C80 56 72 44 60 50Z" ${u(p, 'f')} ${ol(P.red, P)}/>` +
  line('M64 36C72 32 78 38 86 36', '#FFFFFF', 2, 0.5) +
  `<circle cx="58" cy="28" r="4.5" ${u(p, 'n')} ${ol(sun(P), P, 1.4)}/>` +
  sparkle(86, 62, 5, tint(sun(P), 0.4)) +
  sparkle(36, 50, 3.5, tint(sun(P), 0.4), 0.7);

const explorer: IconBuilder = (P, p) => {
  const g = sun(P);
  let ticks = '';
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4;
    const r0 = i % 2 ? 22 : 19;
    ticks += `M${n(64 + r0 * Math.cos(a))} ${n(62 + r0 * Math.sin(a))}L${n(64 + 25 * Math.cos(a))} ${n(62 + 25 * Math.sin(a))}`;
  }
  return (
    D(sh(P, p), ball(`${p}c`, g, 0.5, 0.4), ball(`${p}f`, paper(P), 0.4, 0.12)) +
    gr(p, 64, 104, 30) +
    `<rect x="57" y="18" width="14" height="10" rx="4" ${u(p, 'c')} ${ol(g, P, 1.8)}/>` +
    `<circle cx="64" cy="62" r="37" ${u(p, 'c')} ${ol(g, P, 2.4)}/>` +
    `<circle cx="64" cy="62" r="29" ${u(p, 'f')} stroke="${edge(g, P)}" stroke-width="1.6"/>` +
    line(ticks, shade(P.slate, 0.1), 2, 0.55) +
    `<g transform="rotate(35 64 62)"><path d="M64 36L71 62H57Z" fill="${P.red}" ${ol(P.red, P, 1.4)}/><path d="M64 88L71 62H57Z" fill="${tint(P.blue, 0.15)}" ${ol(P.blue, P, 1.4)}/>` +
    line('M64 40L60 60', '#FFFFFF', 1.6, 0.5) +
    `</g><circle cx="64" cy="62" r="4" fill="${g}" ${ol(g, P, 1.4)}/>` +
    gloss(47, 43, 11, 4.5, -38, 0.55)
  );
};

/** Game Passport: a booklet with a globe on the cover, a round stamp pressed over its corner */
const traveller: IconBuilder = (P, p) => {
  const g = sun(P);
  return (
    D(sh(P, p), body(`${p}b`, P.teal, 0.3, 0.35), body(`${p}q`, paper(P), 0.3, 0.12)) +
    gr(p, 62, 104, 30) +
    `<rect x="40" y="26" width="46" height="62" rx="6" ${u(p, 'q')} ${ol(paper(P), P, 1.6)} transform="rotate(-8 63 57)"/>` +
    `<rect x="36" y="22" width="48" height="66" rx="6" ${u(p, 'b')} ${ol(P.teal, P, 2.2)}/>` +
    `<circle cx="60" cy="50" r="13" fill="none" stroke="${g}" stroke-width="2.4"/>` +
    line('M47 50H73M60 37C53 44 53 56 60 63M60 37C67 44 67 56 60 63', g, 1.8, 0.9) +
    line('M48 74H72', g, 2.4, 0.8) +
    `<g transform="rotate(-16 88 80)"><circle cx="88" cy="80" r="16" fill="${P.red}" fill-opacity=".14" stroke="${P.red}" stroke-width="2.6"/>` +
    `<circle cx="88" cy="80" r="10.5" fill="none" stroke="${P.red}" stroke-width="1.4" stroke-dasharray="3 2.4"/>` +
    `<polygon points="${star(88, 80, 5.5, 2.4, 5)}" fill="${P.red}"/></g>` +
    gloss(48, 30, 9, 3, -30, 0.45)
  );
};

const regular: IconBuilder = (P, p) => D(lanternDefs(P, p)) + roundLantern(P, p, 64, 60, 1.05);

const winStreak: IconBuilder = (P, p) => {
  const g = sun(P);
  return (
    `<defs><linearGradient id="${p}g" x1=".2" y1="0" x2=".7" y2="1"><stop offset="0" stop-color="${tint(g, 0.55)}"/><stop offset=".45" stop-color="${g}"/><stop offset="1" stop-color="${P.orange}"/></linearGradient>` +
    fade(`${p}h`, P.orange, P.dark ? 0.5 : 0.3) +
    `</defs>` +
    line('M30 96Q56 88 90 42', g, 7, 0.22) +
    `<circle cx="88" cy="40" r="28" ${u(p, 'h')}/>` +
    [
      [36, 90, 10],
      [58, 70, 14],
      [86, 42, 21],
    ]
      .map(
        ([x, y, r]) =>
          `<polygon points="${star(x!, y!, r!, r! * 0.36, 4)}" ${u(p, 'g')} ${ol(P.orange, P, 1.8)}/>` +
          gloss(x! - r! * 0.2, y! - r! * 0.25, r! * 0.2, r! * 0.1, -35, 0.7),
      )
      .join('')
  );
};

const dailyWeek: IconBuilder = (P, p) => {
  const pp = paper(P);
  const dots = [
    [43.5, 64],
    [56.5, 64],
    [69.5, 64],
    [82.5, 64],
    [50, 81],
    [63, 81],
  ]
    .map(([x, y]) => `<circle cx="${x}" cy="${y}" r="5" ${u(p, 't')} ${ol(P.teal, P, 1.3)}/>`)
    .join('');
  return (
    D(
      sh(P, p),
      body(`${p}p`, pp, 0.3, 0.15),
      body(`${p}r`, P.red, 0.4, 0.3),
      ball(`${p}t`, P.teal, 0.45, 0.35),
      ball(`${p}o`, sun(P), 0.5, 0.35),
    ) +
    gr(p, 66, 106, 32) +
    `<rect x="35" y="33" width="64" height="68" rx="7" fill="${shade(pp, 0.18)}" ${ol(pp, P, 1.8)}/>` +
    `<path d="M31 37a7 7 0 0 1 7-7h50a7 7 0 0 1 7 7v53l-12 8h-45a7 7 0 0 1-7-7Z" ${u(p, 'p')} ${ol(pp, P, 2)}/>` +
    `<path d="M83 98l12-8h-8a4 4 0 0 0-4 4Z" fill="${shade(pp, 0.12)}" ${ol(pp, P, 1.5)}/>` +
    `<path d="M31 37a7 7 0 0 1 7-7h50a7 7 0 0 1 7 7v11H31Z" ${u(p, 'r')} ${ol(P.red, P, 2)}/>` +
    `<rect x="44" y="23" width="6" height="14" rx="3" fill="${steel(P)}" ${ol(steel(P), P, 1.4)}/><rect x="76" y="23" width="6" height="14" rx="3" fill="${steel(P)}" ${ol(steel(P), P, 1.4)}/>` +
    dots +
    `<polygon points="${star(76, 81, 8, 3.4, 5)}" ${u(p, 'o')} ${ol(sun(P), P, 1.3)}/>` +
    gloss(45, 38, 9, 2.4, -10, 0.5)
  );
};

const level: IconBuilder = (P, p) => {
  let ticks = '';
  for (let i = 0; i < 20; i++) {
    const a = (i / 20) * Math.PI * 2;
    ticks += `M${n(64 + 38 * Math.cos(a))} ${n(64 + 38 * Math.sin(a))}L${n(64 + 41 * Math.cos(a))} ${n(64 + 41 * Math.sin(a))}`;
  }
  return (
    `<defs><linearGradient id="${p}r" x1=".1" y1="0" x2=".9" y2="1"><stop offset="0" stop-color="${tint(P.teal, 0.45)}"/><stop offset=".45" stop-color="${P.teal}"/><stop offset=".8" stop-color="${P.blue}"/><stop offset="1" stop-color="${shade(P.blue, 0.3)}"/></linearGradient>${ball(`${p}g`, sun(P), 0.55, 0.4)}</defs>` +
    `<circle cx="64" cy="64" r="30" fill="none" stroke="${shade(P.blue, P.dark ? 0.55 : 0.1)}" stroke-opacity=".5" stroke-width="13"/>` +
    `<circle cx="64" cy="64" r="30" fill="none" stroke="url(#${p}r)" stroke-width="9"/>` +
    line('M40 48A29 29 0 0 1 56 36', '#FFFFFF', 3.2, 0.55) +
    line(ticks, P.dark ? P.ink : P.blue, 1.6, 0.35) +
    bigStar(P, p, 'g', 64, 66, 19)
  );
};

const friends: IconBuilder = (P, p) =>
  D(
    sh(P, p),
    body(`${p}a`, P.purple, 0.35, 0.35),
    ball(`${p}b`, P.purple, 0.45, 0.35),
    body(`${p}c`, P.teal, 0.35, 0.35),
    ball(`${p}d`, P.teal, 0.45, 0.35),
  ) +
  gr(p, 64, 104, 36) +
  `<path d="M56 98C56 78 66 70 79 70C92 70 102 78 102 98Q79 104 56 98Z" ${u(p, 'a')} ${ol(P.purple, P)}/>` +
  `<circle cx="79" cy="50" r="13" ${u(p, 'b')} ${ol(P.purple, P)}/>` +
  `<path d="M24 102C24 82 36 74 50 74C64 74 76 82 76 102Q50 108 24 102Z" ${u(p, 'c')} ${ol(P.teal, P)}/>` +
  `<circle cx="50" cy="54" r="15" ${u(p, 'd')} ${ol(P.teal, P)}/>` +
  gloss(44, 46, 5, 2.6, -35, 0.6) +
  gloss(74, 43, 4, 2.2, -35, 0.55) +
  gloss(36, 82, 6, 2, -30, 0.4);

const inviter: IconBuilder = (P, p) => {
  const pp = paper(P);
  return (
    D(
      sh(P, p),
      body(`${p}e`, pp, 0.35, 0.18),
      body(`${p}l`, shade(pp, 0.08), 0.25, 0.2, 0.5, 1),
      ball(`${p}h`, P.pink, 0.5, 0.4),
    ) +
    gr(p, 64, 102, 36) +
    `<rect x="24" y="42" width="80" height="54" rx="6" ${u(p, 'e')} ${ol(pp, P, 2.2)}/>` +
    line('M27 92L56 68M101 92L72 68', edge(pp, P), 1.6, 0.6) +
    `<path d="M26 45L64 74L102 45" ${u(p, 'l')} ${ol(pp, P, 2)}/>` +
    `<path d="M64 88C47 77 48 61 57 60C61 60 64 64 64 64C64 64 67 60 71 60C80 61 81 77 64 88Z" ${u(p, 'h')} ${ol(P.pink, P, 1.8)}/>` +
    gloss(58, 66, 3.6, 2, -35, 0.7) +
    gloss(36, 49, 8, 2, -10, 0.5) +
    sparkle(98, 32, 5, tint(sun(P), 0.3)) +
    sparkle(88, 24, 3, tint(sun(P), 0.3), 0.7)
  );
};

const springFestival: IconBuilder = (P, p) => {
  const br = soil(P);
  return (
    D(lanternDefs(P, p)) +
    tube('M108 44C92 36 72 30 34 28', br, P, 3.6) +
    tube('M84 34C90 44 92 52 90 60', br, P, 2.4) +
    roundLantern(P, p, 48, 66, 0.72) +
    blossom(P, 74, 30, 5) +
    blossom(P, 96, 40, 5.5) +
    blossom(P, 90, 60, 4.5) +
    `<circle cx="60" cy="26" r="3" fill="${tint(P.pink, 0.1)}" ${ol(P.pink, P, 1)}/>`
  );
};

const lantern: IconBuilder = (P, p) => {
  const one = (x: number, y: number, w: number, h: number) =>
    `<circle cx="${x}" cy="${n(y - h * 0.4)}" r="${n(h * 0.9)}" ${u(p, 'h')}/>` +
    `<path d="M${n(x - w)} ${n(y - h)}Q${x} ${n(y - h - w * 0.8)} ${n(x + w)} ${n(y - h)}L${n(x + w * 0.62)} ${y}H${n(x - w * 0.62)}Z" ${u(p, 'l')} ${ol(P.orange, P, 1.6)}/>` +
    line(`M${x} ${n(y - h - w * 0.4)}V${y}`, shade(P.orange, 0.25), 1.2, 0.5) +
    `<ellipse cx="${x}" cy="${y}" rx="${n(w * 0.62)}" ry="${n(w * 0.16)}" fill="${tint(sun(P), 0.6)}"/>` +
    gloss(x - w * 0.45, y - h * 0.7, w * 0.18, h * 0.22, 8, 0.45);
  return (
    `<defs><linearGradient id="${p}l" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${shade(P.orange, 0.12)}"/><stop offset=".55" stop-color="${P.orange}"/><stop offset="1" stop-color="${tint(sun(P), 0.45)}"/></linearGradient>${fade(`${p}h`, sun(P), P.dark ? 0.35 : 0.25)}</defs>` +
    sparkle(34, 36, 3, tint(sun(P), 0.5), 0.7) +
    sparkle(58, 26, 2.4, tint(sun(P), 0.5), 0.6) +
    one(92, 46, 8, 16) +
    one(42, 66, 11, 22) +
    one(72, 98, 17, 34)
  );
};

const dragonBoat: IconBuilder = (P, p) => {
  const g = sun(P);
  const wave = shade(P.blue, P.dark ? 0.1 : 0);
  return (
    D(
      clip(p),
      body(`${p}b`, P.red, 0.4, 0.35, 0.4, 1),
      body(`${p}d`, P.green, 0.45, 0.35),
      body(`${p}w`, wave, 0.3, 0.35, 0.3, 1),
    ) +
    `<g clip-path="url(#${p}k)">` +
    `<path d="M24 72C16 62 20 48 32 50C27 56 29 64 38 70Z" ${u(p, 'd')} ${ol(P.green, P, 1.6)}/>` +
    `<path d="M22 70H94L88 84C82 90 72 92 62 92H40C30 92 25 84 22 70Z" ${u(p, 'b')} ${ol(P.red, P, 2)}/>` +
    `<path d="M24 71H93" stroke="${g}" stroke-width="4" stroke-linecap="round"/>` +
    `<path d="${[34, 46, 58, 70].map((x) => `M${x - 5} 81a5 5 0 0 1 10 0`).join('')}" fill="none" stroke="${g}" stroke-width="1.8" stroke-opacity=".8"/>` +
    line('M44 66L36 88M60 66L52 88M76 66L68 88', sun(P), 2.4, 0.9) +
    `<ellipse cx="30" cy="66" rx="5" ry="3.5" fill="${g}" ${ol(g, P, 1.2)}/>` +
    `<path d="M84 72C90 62 90 54 88 48L96 44L102 52C100 60 98 66 94 72Z" ${u(p, 'd')} ${ol(P.green, P, 1.8)}/>` +
    `<path d="M88 50L80 48L86 56L80 58L88 62" fill="none" stroke="${g}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>` +
    `<path d="M88 48C88 38 100 34 110 40L112 46L104 47L110 51L100 54C94 55 89 53 88 48Z" ${u(p, 'd')} ${ol(P.green, P, 1.8)}/>` +
    `<path d="M94 38L90 28L99 36Z" fill="${g}" ${ol(g, P, 1.2)}/>` +
    `<circle cx="99" cy="42" r="2.6" fill="#FFFFFF"/><circle cx="99.6" cy="42" r="1.3" fill="${shade(P.ink, P.dark ? 0.85 : 0)}"/>` +
    `<path d="M10 90Q20 84 30 90T50 90T70 90T90 90T110 90T130 90V120H10Z" ${u(p, 'w')} ${ol(wave, P, 1.6)}/>` +
    line('M22 97Q28 94 34 97M62 100Q68 97 74 100M86 96Q90 94 94 96', '#FFFFFF', 1.6, 0.5) +
    `</g>` +
    gloss(46, 75, 12, 2, -2, 0.45) +
    gloss(96, 39, 4, 1.8, -25, 0.55)
  );
};

const midAutumn: IconBuilder = (P, p) => {
  const cake = mix(P.orange, P.gold, 0.45);
  let sc = '';
  const k = 14;
  for (let i = 0; i <= k; i++) {
    const a = (i / k) * Math.PI * 2;
    const r = 26;
    const x = 54 + r * Math.cos(a);
    const y = 78 + r * Math.sin(a) * 0.92;
    if (i === 0) sc += `M${n(x)} ${n(y)}`;
    else {
      const am = a - Math.PI / k;
      sc += `Q${n(54 + (r + 5) * Math.cos(am))} ${n(78 + (r + 5) * Math.sin(am) * 0.92)} ${n(x)} ${n(y)}`;
    }
  }
  const petals = [0, 1, 2, 3]
    .map((i) => `<ellipse cx="54" cy="70" rx="3.6" ry="7" transform="rotate(${i * 90 + 45} 54 78)"/>`)
    .join('');
  return (
    D(
      sh(P, p),
      ball(`${p}m`, P.dark ? tint(P.gold, 0.55) : lift(P.gold, 0.3), 0.4, 0.25),
      ball(`${p}c`, cake, 0.4, 0.4),
      ball(`${p}i`, shade(cake, 0.08), 0.2, 0.3),
      fade(`${p}h`, tint(P.gold, 0.4), P.dark ? 0.45 : 0.5),
    ) +
    `<circle cx="78" cy="44" r="36" ${u(p, 'h')}/>` +
    `<circle cx="78" cy="44" r="22" ${u(p, 'm')} ${ol(tint(P.gold, 0.4), P, 1.4)}/>` +
    `<g fill="${shade(tint(P.gold, 0.55), 0.12)}" fill-opacity=".5"><circle cx="84" cy="38" r="4"/><circle cx="74" cy="52" r="3"/><circle cx="88" cy="52" r="2"/></g>` +
    gloss(70, 34, 6, 3, -35, 0.5) +
    gr(p, 56, 106, 30) +
    `<path d="${sc}Z" ${u(p, 'c')} ${ol(cake, P, 2)}/>` +
    `<ellipse cx="54" cy="78" rx="18" ry="16.5" ${u(p, 'i')} stroke="${edge(cake, P)}" stroke-width="1.4" stroke-opacity=".6"/>` +
    `<g fill="${tint(cake, 0.3)}" fill-opacity=".8" stroke="${edge(cake, P)}" stroke-width="1" stroke-opacity=".5">${petals}</g>` +
    `<circle cx="54" cy="78" r="3.4" fill="${tint(cake, 0.35)}" ${ol(cake, P, 1)}/>` +
    gloss(42, 62, 8, 3, -30, 0.5)
  );
};

const podium: IconBuilder = (P, p) => {
  const step = (x: number, y: number, w: number, c: string, k: string, pips: number) => {
    let d = '';
    for (let i = 0; i < pips; i++)
      d += `<circle cx="${n(x + w / 2 + (i - (pips - 1) / 2) * 7)}" cy="${y + 18}" r="2.6"/>`;
    return (
      `<rect x="${x}" y="${y}" width="${w}" height="${100 - y}" rx="3" ${u(p, k)} ${ol(c, P, 1.8)}/>` +
      `<rect x="${x + 2}" y="${y + 2}" width="${w - 4}" height="5" rx="2" fill="#FFFFFF" fill-opacity=".3"/>` +
      `<g fill="#FFFFFF" fill-opacity=".85">${d}</g>`
    );
  };
  return (
    D(
      sh(P, p),
      body(`${p}a`, P.teal, 0.35, 0.35),
      body(`${p}b`, P.purple, 0.35, 0.35),
      body(`${p}c`, P.blue, 0.35, 0.35),
      ball(`${p}g`, sun(P), 0.55, 0.4),
    ) +
    gr(p, 64, 102, 40) +
    step(26, 68, 26, P.purple, 'b', 2) +
    step(76, 76, 26, P.blue, 'c', 3) +
    step(50, 52, 28, P.teal, 'a', 1) +
    bigStar(P, p, 'g', 64, 34, 14)
  );
};

const bigWin: IconBuilder = (P, p) => {
  const g = sun(P);
  return (
    `<defs><linearGradient id="${p}t" x1="0" y1="1" x2="1" y2="0"><stop offset="0" stop-color="${P.teal}" stop-opacity="0"/><stop offset=".6" stop-color="${P.teal}" stop-opacity=".75"/><stop offset="1" stop-color="${tint(P.teal, 0.4)}"/></linearGradient>` +
    body(`${p}a`, g, 0.5, 0.35, 0.6, 1) +
    fade(`${p}h`, g, P.dark ? 0.5 : 0.3) +
    `</defs>` +
    `<circle cx="86" cy="42" r="26" ${u(p, 'h')}/>` +
    `<path d="M26 100L36 106L84 54L74 44Z" ${u(p, 't')}/>` +
    `<path d="M22 86L26 88L64 50L60 48Z" ${u(p, 't')} opacity=".6"/>` +
    `<path d="M52 108L56 110L90 74L86 72Z" ${u(p, 't')} opacity=".5"/>` +
    `<path d="M100 28L94 64L85 55L77 63L69 55L77 47L68 38Z" ${u(p, 'a')} ${ol(g, P, 2)}/>` +
    gloss(84, 38, 7, 2.6, -40, 0.6) +
    sparkle(44, 44, 6, tint(g, 0.3)) +
    sparkle(98, 82, 5, tint(g, 0.3), 0.8) +
    sparkle(34, 64, 3, tint(g, 0.3), 0.6)
  );
};

const liftoffOrbit: IconBuilder = (P, p) => {
  const hull = P.dark ? P.ink : tint(P.blue, 0.85);
  return (
    D(
      ball(`${p}p`, P.purple, 0.45, 0.4),
      body(`${p}h`, hull, 0.5, 0.25, 0.3, 1),
      body(`${p}n`, P.teal, 0.35, 0.3),
      body(`${p}f`, P.orange, 0.3, 0.3),
    ) +
    `<ellipse cx="60" cy="70" rx="42" ry="14" transform="rotate(-18 60 70)" fill="none" stroke="${P.teal}" stroke-width="2.4" stroke-opacity=".5" stroke-dasharray="1 6" stroke-linecap="round"/>` +
    `<circle cx="58" cy="72" r="23" ${u(p, 'p')} ${ol(P.purple, P, 2)}/>` +
    `<path d="M44 64Q58 60 76 66M42 80Q56 76 72 84" fill="none" stroke="${shade(P.purple, 0.25)}" stroke-width="2.4" stroke-opacity=".45" stroke-linecap="round"/>` +
    gloss(49, 62, 7, 3.5, -35, 0.55) +
    `<path d="M20 84A42 14 -18 0 0 102 57" fill="none" stroke="${P.teal}" stroke-width="3" stroke-linecap="round"/>` +
    // rocket (points up-right along the orbit)
    `<g transform="translate(94 40) rotate(-40)">` +
    `<path d="M-14 -5L-20 -13L-8 -5ZM-14 5L-20 13L-8 5Z" ${u(p, 'f')} ${ol(P.orange, P, 1.2)}/>` +
    `<path d="M-16 -6H4C12 -6 18 -2 20 0C18 2 12 6 4 6H-16Q-18 6 -18 0Q-18 -6 -16 -6Z" ${u(p, 'h')} ${ol(hull, P, 1.4)}/>` +
    `<path d="M8 -5.6C14 -4 18 -2 19.6 0C18 2 14 4 8 5.6Q10 0 8 -5.6Z" ${u(p, 'n')}/>` +
    `<circle cx="-2" cy="0" r="2.6" fill="${P.sky}" ${ol(P.sky, P, 1)}/>` +
    `<path d="M-19 -3Q-27 0 -19 3Z" fill="${tint(sun(P), 0.4)}"/>` +
    `</g>` +
    sparkle(30, 38, 3.4, tint(sun(P), 0.4), 0.8) +
    sparkle(100, 88, 2.6, tint(sun(P), 0.4), 0.7)
  );
};

const rouletteGreen: IconBuilder = (P, p) => {
  const blk = P.dark ? mix(P.slate, P.ink, 0.12) : P.slate;
  return (
    D(
      clip(p),
      body(`${p}r`, P.red, 0.35, 0.35),
      body(`${p}b`, blk, 0.2, 0.35),
      body(`${p}g`, P.green, 0.45, 0.35),
      fade(`${p}h`, P.green, P.dark ? 0.55 : 0.35),
      ball(`${p}o`, sun(P), 0.5, 0.35),
    ) +
    `<g clip-path="url(#${p}k)">` +
    `<rect x="-4" y="48" width="24" height="34" rx="5" ${u(p, 'b')} ${ol(blk, P, 1.6)}/>` +
    `<rect x="22" y="48" width="24" height="34" rx="5" ${u(p, 'r')} ${ol(P.red, P, 1.6)}/>` +
    `<rect x="82" y="48" width="24" height="34" rx="5" ${u(p, 'b')} ${ol(blk, P, 1.6)}/>` +
    `<rect x="108" y="48" width="24" height="34" rx="5" ${u(p, 'r')} ${ol(P.red, P, 1.6)}/>` +
    `</g>` +
    `<circle cx="64" cy="65" r="30" ${u(p, 'h')}/>` +
    `<rect x="46" y="40" width="36" height="50" rx="7" ${u(p, 'g')} ${ol(P.green, P, 2.2)}/>` +
    `<polygon points="64,53 75,65 64,77 53,65" fill="#FFFFFF" fill-opacity=".85" ${ol(P.green, P, 1.4)}/>` +
    gloss(55, 47, 6, 2.4, -20, 0.6) +
    `<path d="M55 24H73L64 36Z" ${u(p, 'o')} ${ol(sun(P), P, 1.6)}/>` +
    `<path d="M64 102V94" stroke="${sun(P)}" stroke-width="3" stroke-linecap="round"/>`
  );
};

const potWinner: IconBuilder = (P, p) => {
  const c = P.purple;
  const g = sun(P);
  return (
    D(
      sh(P, p),
      ball(`${p}b`, c, 0.45, 0.4),
      body(`${p}n`, shade(c, 0.1), 0.3, 0.3),
      body(`${p}r`, g, 0.5, 0.35),
      ball(`${p}g`, g, 0.55, 0.4),
      fade(`${p}h`, g, P.dark ? 0.55 : 0.35),
    ) +
    gr(p, 64, 106, 32) +
    `<circle cx="64" cy="32" r="22" ${u(p, 'h')}/>` +
    `<path d="M46 58C30 66 30 96 48 102H80C98 96 98 66 82 58Z" ${u(p, 'b')} ${ol(c, P, 2.2)}/>` +
    `<path d="M40 82Q64 90 88 82" fill="none" stroke="${g}" stroke-width="3" stroke-opacity=".8"/>` +
    `<rect x="46" y="50" width="36" height="10" rx="3" ${u(p, 'n')} ${ol(c, P, 1.8)}/>` +
    `<rect x="40" y="44" width="48" height="9" rx="4.5" ${u(p, 'r')} ${ol(g, P, 1.8)}/>` +
    gloss(46, 70, 6, 10, 20, 0.45) +
    bigStar(P, p, 'g', 64, 28, 14)
  );
};

const sweetJackpot: IconBuilder = (P, p) => {
  const glass = P.dark ? tint(P.sky, 0.2) : tint(P.sky, 0.55);
  const lid = P.pink;
  const candies = [
    [50, 90, 't'],
    [66, 92, 'o'],
    [81, 88, 'q'],
    [57, 76, 'q'],
    [74, 75, 't'],
    [64, 62, 'o'],
  ] as const;
  return (
    D(
      sh(P, p),
      body(`${p}l`, lid, 0.4, 0.35),
      ball(`${p}t`, P.teal, 0.5, 0.35),
      ball(`${p}o`, P.orange, 0.5, 0.35),
      ball(`${p}q`, P.purple, 0.5, 0.35),
    ) +
    gr(p, 64, 106, 32) +
    `<rect x="36" y="42" width="56" height="62" rx="15" fill="${glass}" fill-opacity=".28" ${ol(P.sky, P, 2)}/>` +
    candies
      .map(
        ([x, y, k]) =>
          `<circle cx="${x}" cy="${y}" r="8" ${u(p, k)} ${ol(k === 't' ? P.teal : k === 'o' ? P.orange : P.purple, P, 1.3)}/>`,
      )
      .join('') +
    `<rect x="36" y="42" width="56" height="62" rx="15" fill="${glass}" fill-opacity=".12"/>` +
    `<path d="M44 56V92" stroke="#FFFFFF" stroke-opacity=".55" stroke-width="4" stroke-linecap="round"/>` +
    `<rect x="42" y="36" width="44" height="9" rx="3" fill="${shade(lid, 0.15)}" ${ol(lid, P, 1.6)}/>` +
    `<path d="M40 38Q40 24 64 24Q88 24 88 38Z" ${u(p, 'l')} ${ol(lid, P, 2)}/>` +
    `<circle cx="64" cy="21" r="5" fill="${sun(P)}" ${ol(sun(P), P, 1.4)}/>` +
    gloss(52, 29, 7, 2.4, -12, 0.55)
  );
};

const koi88: IconBuilder = (P, p) => {
  const gold = sun(P);
  const water = P.dark ? mix(P.blue, P.sky, 0.4) : tint(P.sky, 0.3);
  return (
    D(body(`${p}f`, gold, 0.5, 0.35, 0.6, 1), ball(`${p}w`, water, 0.35, 0.45)) +
    `<circle cx="64" cy="64" r="38" ${u(p, 'w')} ${ol(water, P, 2)}/>` +
    `<circle cx="64" cy="64" r="30" fill="none" stroke="#FFFFFF" stroke-opacity=".22" stroke-width="2"/>` +
    `<circle cx="64" cy="64" r="21" fill="none" stroke="#FFFFFF" stroke-opacity=".16" stroke-width="2"/>` +
    line('M34 74Q40 70 46 74T58 74M76 90Q82 86 88 90T100 90', '#FFFFFF', 2, 0.45) +
    gloss(44, 42, 10, 4, -38, 0.4) +
    koi(
      P,
      p,
      'rotate(-28 64 64) translate(2 4) scale(.92) translate(5 5)',
      gold,
      P.dark ? tint(P.red, 0.1) : P.red,
    )
  );
};

const workshopMaster: IconBuilder = (P, p) => {
  const g = sun(P);
  const st = steel(P);
  const wood = mix(P.orange, soil(P), 0.3);
  return (
    D(
      sh(P, p),
      ball(`${p}g`, g, 0.5, 0.4),
      body(`${p}w`, wood, 0.4, 0.3, 1, 0.3),
      body(`${p}m`, st, 0.45, 0.35),
    ) +
    gr(p, 64, 106, 32) +
    `<path d="${gearPath(58, 62, 34, 27, 10)}" ${u(p, 'g')} ${ol(g, P, 2.2)}/>` +
    `<circle cx="58" cy="62" r="17" fill="none" stroke="${edge(g, P)}" stroke-opacity=".45" stroke-width="2"/>` +
    `<circle cx="58" cy="62" r="8" fill="${P.dark ? shade(P.slate, 0.3) : tint(P.slate, 0.75)}" ${ol(g, P, 2)}/>` +
    gloss(44, 44, 9, 4, -35, 0.55) +
    `<g transform="rotate(38 76 72)">` +
    `<rect x="72" y="58" width="8" height="46" rx="3.5" ${u(p, 'w')} ${ol(wood, P, 1.6)}/>` +
    `<path d="M60 48H92Q95 48 95 51V61Q95 64 92 64H60Q56 64 55 60V52Q56 48 60 48Z" ${u(p, 'm')} ${ol(st, P, 1.8)}/>` +
    `<rect x="60" y="50.5" width="26" height="3" rx="1.5" fill="#FFFFFF" fill-opacity=".5"/>` +
    `</g>`
  );
};

const carpGate: IconBuilder = (P, p) => {
  const red = P.red;
  const roof = P.teal;
  const g = sun(P);
  const water = shade(P.blue, P.dark ? 0.1 : 0);
  return (
    D(
      clip(p),
      body(`${p}p`, red, 0.35, 0.35, 1, 0.3),
      body(`${p}r`, roof, 0.4, 0.35),
      body(`${p}f`, g, 0.5, 0.35, 0.6, 1),
      body(`${p}w`, water, 0.3, 0.35, 0.3, 1),
    ) +
    `<g clip-path="url(#${p}k)">` +
    `<rect x="34" y="72" width="9" height="36" rx="2" ${u(p, 'p')} ${ol(red, P, 1.6)}/>` +
    `<rect x="85" y="72" width="9" height="36" rx="2" ${u(p, 'p')} ${ol(red, P, 1.6)}/>` +
    `<rect x="30" y="76" width="68" height="6" rx="2" ${u(p, 'p')} ${ol(red, P, 1.4)}/>` +
    `<path d="M16 60Q24 67 32 64H96Q104 67 112 60L106 72H22Z" ${u(p, 'r')} ${ol(roof, P, 1.8)}/>` +
    `<path d="M22 58Q64 53 106 58" fill="none" stroke="${edge(roof, P)}" stroke-width="4" stroke-linecap="round"/>` +
    `<circle cx="18" cy="57" r="3.4" fill="${g}" ${ol(g, P, 1.2)}/><circle cx="110" cy="57" r="3.4" fill="${g}" ${ol(g, P, 1.2)}/>` +
    `<path d="${[36, 46, 56, 72, 82, 92].map((x) => `M${x} 63V70`).join('')}" stroke="${shade(roof, 0.3)}" stroke-width="1.6" stroke-opacity=".6"/>` +
    `<path d="M30 64H98" stroke="${g}" stroke-width="2.4" stroke-linecap="round"/>` +
    `<rect x="58" y="70" width="12" height="8" rx="1.5" fill="${g}" ${ol(g, P, 1.2)}/>` +
    `<path d="M10 102Q22 95 34 102T58 102T82 102T106 102T130 102V120H10Z" ${u(p, 'w')} ${ol(water, P, 1.6)}/>` +
    `</g>` +
    line('M30 96Q30 76 42 62', tint(P.sky, 0.3), 2.4, 0.55) +
    koi(
      P,
      p,
      'translate(64 38) rotate(-32) scale(.62) translate(-64 -60)',
      g,
      P.dark ? tint(P.red, 0.1) : P.red,
    ) +
    sparkle(98, 30, 4.5, tint(g, 0.3), 0.85)
  );
};

const firecrackersClear: IconBuilder = (P, p) => {
  const g = sun(P);
  const pts = [
    [58, 40, -1],
    [62, 52, 1],
    [58, 64, -1],
    [62, 76, 1],
    [58, 88, -1],
  ] as const;
  const crackers = pts
    .map(
      ([x, y, s]) =>
        `<g transform="rotate(${s * 62} ${x} ${y})"><rect x="${x - 5}" y="${y}" width="10" height="22" rx="3" ${u(p, 'c')} ${ol(P.red, P, 1.4)}/>` +
        `<rect x="${x - 5}" y="${y + 17}" width="10" height="4" fill="${g}" fill-opacity=".9"/><rect x="${x - 2.5}" y="${y + 3}" width="2" height="12" rx="1" fill="#FFFFFF" fill-opacity=".45"/></g>`,
    )
    .join('');
  return (
    D(sh(P, p), body(`${p}c`, P.red, 0.4, 0.35, 1, 0.2), ball(`${p}k`, g, 0.5, 0.35)) +
    gr(p, 64, 108, 22, 3) +
    tube('M60 24C62 50 58 76 60 96', mix(P.red, g, 0.5), P, 2) +
    crackers +
    `<path d="M54 22L66 22L60 30Z" ${u(p, 'k')} ${ol(g, P, 1.4)}/>` +
    `<circle cx="60" cy="20" r="5" ${u(p, 'k')} ${ol(g, P, 1.4)}/>` +
    // loose, untied fuse (no spark)
    line('M60 96C62 102 70 100 72 106C74 110 80 108 84 104', soil(P), 2.2) +
    line('M84 104C88 100 86 96 90 94', soil(P), 2.2, 0.6)
  );
};

const hongbaoLuckiest: IconBuilder = (P, p) => {
  const g = sun(P);
  return (
    D(
      sh(P, p),
      body(`${p}e`, P.red, 0.35, 0.35),
      body(`${p}f`, shade(P.red, 0.12), 0.25, 0.3),
      ball(`${p}g`, g, 0.55, 0.4),
    ) +
    gr(p, 64, 106, 30) +
    `<rect x="38" y="22" width="52" height="80" rx="7" ${u(p, 'e')} ${ol(P.red, P, 2.2)}/>` +
    `<rect x="43" y="27" width="42" height="70" rx="4" fill="none" stroke="${g}" stroke-opacity=".55" stroke-width="1.4"/>` +
    `<path d="M38 29a7 7 0 0 1 7-7h38a7 7 0 0 1 7 7v14Q64 62 38 43Z" ${u(p, 'f')} ${ol(P.red, P, 2)}/>` +
    `<circle cx="64" cy="54" r="10" ${u(p, 'g')} ${ol(g, P, 1.8)}/>` +
    `<circle cx="64" cy="54" r="5.5" fill="none" stroke="${edge(g, P)}" stroke-width="1.4" stroke-opacity=".6"/>` +
    `<path d="M50 84H78M54 90H74" stroke="${g}" stroke-opacity=".6" stroke-width="2" stroke-linecap="round"/>` +
    gloss(46, 32, 6, 2.4, -20, 0.5) +
    gloss(60, 50, 3, 1.6, -35, 0.7) +
    sparkle(98, 34, 6, tint(g, 0.3)) +
    sparkle(28, 76, 4, tint(g, 0.3), 0.7)
  );
};

const mahjongHand: IconBuilder = (P, p) => {
  const face = paper(P);
  const back = P.green;
  const tile = (rot: number, dots: number[][], cols: string[]) =>
    `<g transform="rotate(${rot} 64 112)">` +
    `<rect x="48" y="34" width="32" height="44" rx="5" fill="${shade(back, 0.05)}" ${ol(back, P, 1.8)}/>` +
    `<rect x="48" y="30" width="32" height="42" rx="5" ${u(p, 'f')} ${ol(face, P, 1.8)}/>` +
    dots
      .map(
        ([x, y, r], i) =>
          `<circle cx="${x}" cy="${y}" r="${r}" fill="${cols[i]}" ${ol(cols[i]!, P, 1.2)}/><circle cx="${x}" cy="${y}" r="${n(r! * 0.4)}" fill="#FFFFFF" fill-opacity=".7"/>`,
      )
      .join('') +
    `</g>`;
  return (
    D(sh(P, p), body(`${p}f`, face, 0.35, 0.18)) +
    gr(p, 64, 104, 36) +
    tile(
      -24,
      [
        [58, 42, 4.5],
        [64, 51, 4.5],
        [70, 60, 4.5],
      ],
      [P.blue, P.red, P.green],
    ) +
    tile(
      24,
      [
        [64, 42, 4.5],
        [64, 60, 4.5],
      ],
      [P.green, P.blue],
    ) +
    tile(0, [[64, 51, 9]], [P.red]) +
    gloss(55, 36, 5, 2, -20, 0.6)
  );
};

const zhajinhuaLeopard: IconBuilder = (P, p) => {
  const face = P.dark ? tint(P.ink, 0.4) : '#FFFFFF';
  const card = (rot: number) =>
    `<g transform="rotate(${rot} 64 116)">` +
    `<rect x="46" y="28" width="36" height="52" rx="5" ${u(p, 'c')} ${ol(face, P, 1.8)}/>` +
    `<rect x="50" y="32" width="28" height="44" rx="3" fill="none" stroke="${P.red}" stroke-opacity=".35" stroke-width="1.2"/>` +
    `<circle cx="64" cy="54" r="10" ${u(p, 'r')} ${ol(P.red, P, 1.3)}/>` +
    `<g fill="${tint(sun(P), 0.3)}">${[0, 1, 2, 3, 4].map((i) => `<circle cx="${n(64 + 4.2 * Math.cos(i * 1.2566 - 1.5708))}" cy="${n(54 + 4.2 * Math.sin(i * 1.2566 - 1.5708))}" r="3"/>`).join('')}</g><circle cx="64" cy="54" r="2" fill="${P.red}"/>` +
    `</g>`;
  return (
    D(sh(P, p), body(`${p}c`, face, 0.3, 0.15), body(`${p}r`, P.red, 0.4, 0.3)) +
    gr(p, 64, 104, 36) +
    card(-22) +
    card(22) +
    card(0) +
    gloss(53, 34, 5, 2, -20, 0.6)
  );
};

export const ICONS: Record<AchievementId, IconBuilder> = {
  'first-round': firstRound,
  'first-win': firstWin,
  explorer,
  traveller,
  regular,
  'win-streak': winStreak,
  'daily-week': dailyWeek,
  level,
  friends,
  inviter,
  'spring-festival': springFestival,
  lantern,
  'dragon-boat': dragonBoat,
  'mid-autumn': midAutumn,
  podium,
  'big-win': bigWin,
  'liftoff-orbit': liftoffOrbit,
  'roulette-green': rouletteGreen,
  'pot-winner': potWinner,
  'sweet-jackpot': sweetJackpot,
  'koi-88': koi88,
  'workshop-master': workshopMaster,
  'carp-gate': carpGate,
  'firecrackers-clear': firecrackersClear,
  'hongbao-luckiest': hongbaoLuckiest,
  'mahjong-hand': mahjongHand,
  'zhajinhua-leopard': zhajinhuaLeopard,
};

/* ---------------- moment icons ---------------- */

const momentWin: IconBuilder = (P, p) => {
  const g = sun(P);
  return (
    D(body(`${p}a`, g, 0.5, 0.35, 0.6, 1), fade(`${p}h`, g, P.dark ? 0.45 : 0.3)) +
    `<circle cx="64" cy="54" r="34" ${u(p, 'h')}/>` +
    `<ellipse cx="64" cy="70" rx="38" ry="12" fill="none" stroke="${P.teal}" stroke-width="3" stroke-opacity=".45" transform="rotate(-12 64 70)"/>` +
    `<path d="M64 22L90 52H74V92H54V52H38Z" ${u(p, 'a')} ${ol(g, P, 2.2)}/>` +
    `<path d="M26 78A38 12 -12 0 0 102 62" fill="none" stroke="${P.teal}" stroke-width="3.4" stroke-linecap="round"/>` +
    `<circle cx="102" cy="62" r="4" fill="${tint(P.teal, 0.3)}" ${ol(P.teal, P, 1.2)}/>` +
    gloss(58, 36, 7, 2.6, -48, 0.6) +
    sparkle(34, 40, 4, tint(g, 0.3), 0.8)
  );
};

const momentPlace: IconBuilder = (P, p) => {
  const leafC = P.green;
  let leaves = '';
  for (const s of [-1, 1]) {
    for (let i = 0; i < 6; i++) {
      const a = Math.PI / 2 + s * (0.45 + i * 0.42);
      const x = 64 + 34 * Math.cos(a);
      const y = 60 + 34 * Math.sin(a);
      const rot = (a * 180) / Math.PI + (s > 0 ? 90 : -90) + s * 30;
      leaves += `<ellipse cx="${n(x)}" cy="${n(y)}" rx="4.6" ry="10" transform="rotate(${n(rot)} ${n(x)} ${n(y)})"/>`;
    }
  }
  return (
    D(body(`${p}l`, leafC, 0.45, 0.35), ball(`${p}g`, sun(P), 0.55, 0.4), body(`${p}r`, P.red, 0.4, 0.3)) +
    `<path d="M${n(64 - 34 * Math.sin(0.45))} ${n(60 + 34 * Math.cos(0.45))}A34 34 0 1 1 ${n(64 + 34 * Math.sin(0.45))} ${n(60 + 34 * Math.cos(0.45))}" fill="none" stroke="${edge(leafC, P)}" stroke-width="2.4"/>` +
    `<g ${u(p, 'l')} ${ol(leafC, P, 1.2)}>${leaves}</g>` +
    `<path d="M56 90L48 108L56 104L60 110L64 94ZM72 90L80 108L72 104L68 110L64 94Z" ${u(p, 'r')} ${ol(P.red, P, 1.4)}/>` +
    `<circle cx="64" cy="92" r="5" ${u(p, 'r')} ${ol(P.red, P, 1.4)}/>` +
    bigStar(P, p, 'g', 64, 58, 17)
  );
};

export const MOMENT_ICONS: Record<'win' | 'place', IconBuilder> = { win: momentWin, place: momentPlace };
